/**
 * Resolves a single stable identifier for the current visitor. Prefers
 * Microsoft Clarity's own id (its `_clck` cookie) so this device/browser can
 * be cross-referenced with a Clarity session recording *if* Clarity is ever
 * added to this app — but works standalone today: falls back to a locally
 * generated, localStorage-persisted id when Clarity isn't present/blocked,
 * so callers always get *something* stable rather than null.
 */

const FALLBACK_ID_KEY = "ksa_user_id";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Clarity's `_clck` cookie is formatted "<clarityUserId>|<year>|<count>|<...>".
 * The first segment is the stable per-browser Clarity user id (distinct from
 * `_clsk`, which is per-session).
 */
export function getClarityId() {
  const clck = readCookie("_clck");
  if (!clck) return null;
  const id = clck.split("|")[0];
  return id || null;
}

function getOrCreateFallbackId() {
  try {
    let id = window.localStorage.getItem(FALLBACK_ID_KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(FALLBACK_ID_KEY, id);
    }
    return id;
  } catch {
    return "no-local-storage";
  }
}

/**
 * The identifier to use as "the" per-user id everywhere (logs). Re-reads the
 * cookie on every call (cheap) so that if Clarity (or an equivalent) is ever
 * added later, this automatically starts picking up the real id instead of
 * the fallback, with no code changes needed here.
 */
export function getUserIdentifier() {
  return getClarityId() || getOrCreateFallbackId();
}
